/**
 * useInbox — the SINGLE shared inbox state core (Option 3).
 *
 * This is the one source of truth for the inbox, instantiated EXACTLY ONCE by
 * `InboxProvider` (`presentation/contexts/InboxContext.tsx`), which is
 * mounted in `ProtectedRoutesWrapper` ABOVE the `KeepAliveOutlet` route cache so
 * it renders once per authenticated session and is not duplicated per cached
 * route. Both the TopBar unseen-badge driver (`HomeTopBarHeader`) and the
 * lazily-routed `InboxPage` read this same instance through the provider — they
 * no longer each
 * spin up an independent `useInbox`/sub-hook pair, so the first notifications
 * page and the first chat-nodes page are fetched ONCE and only ONE chat
 * message-nodes subscription is ever open. (Before this refactor the app-level
 * badge driver and the Inbox page each fetched both first pages independently.)
 *
 * It composes the two cohesive sub-hooks (`useInboxNotifications`,
 * `useInboxChat`) and owns the cross-cutting concerns Flutter's MessagesScreen
 * `State` kept at the top level:
 *   - the auth gate (reads the current user from Redux and feeds
 *     `firebaseId` into both sub-hooks; the sub-hooks stay parameterized and
 *     testable in isolation),
 *   - the unified, memoized `items: InboxItem[]` view-model reproducing the
 *     sliver order of `messages_screen.dart:704-768`
 *     (NotificationsListHeader → notifications → MoreNotificationsWidget →
 *     ChatListHeader → chat list),
 *   - the STARTUP init (`_initData` side-effect ordering,
 *     `messages_screen.dart:95-104`): fire `getChatNodes()` without awaiting so
 *     the chat fetch drives `isFirstLoad`, then `getNotifications()`. This runs
 *     ONCE per resolved user id at provider mount — the web analogue of Flutter
 *     keeping `MessagesScreen` permanently in the `MainScreen` `IndexedStack`
 *     (so `_initData` runs at app startup regardless of the active tab). Opening
 *     `/inbox` does NOT re-fire it.
 *   - the combined unseen-badge truth (`hasUnseenInbox`, porting
 *     `_checkNotificationsUpdatesBadge` / `_checkChatUpdatesBadge`,
 *     `messages_screen.dart:574-613`; web replaces Flutter's
 *     `NewChatMessageNotifier` with one shared derived boolean the TopBar reads
 *     off this core through `useInboxContext`, NOT an event-bus emit — finding
 *     5). Because this core (and therefore the chat subscription it starts) is
 *     always mounted, an incoming chat message flips `hasUnreadMessage`, the
 *     derived `hasUnseenInbox` re-computes, and every cached TopBar re-renders
 *     the badge LIVE even while the Inbox page is closed.
 *
 * Chat subscription lifetime: `useInboxChat` starts ONE live subscription on the
 * first chat fetch and tears it down only when THIS core unmounts (i.e. the
 * provider unmounts — effectively app shutdown / sign-out). Navigating in and
 * out of `/inbox` does NOT tear it down, so the badge stays live afterwards.
 * `InboxPage` therefore wires NO `onDispose` teardown.
 *
 * This hook NEVER calls `useBasePage` (page-only per `presentation.md`) and NEVER
 * dispatches a slice setter. It does NOT own or clear `isFirstLoad` — that flag is
 * chat-owned (`useInboxChat`, cleared by the chat first-load fetch per
 * `messages_screen.dart:318/460`); this core only surfaces it.
 *
 * Deviations from plan: the plan referenced a `selectCurrentUser` auth selector.
 * No such selector exists in the project; the established pattern is the inline
 * `useSelector((state: RootState) => state.auth.user)` (used by useChat,
 * useUserDetails, useSavedUsers, etc.). This hook follows that pattern.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { InboxItemType } from '@/domain/enums/inbox/inboxItemType';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { refreshAuthUserUseCase } from '@/domain/usecases/user/refreshAuthUserUseCase';
import type { InboxItem } from '../types/inboxItem';
import { useInboxNotifications } from './useInboxNotifications';
import { useInboxChat } from './useInboxChat';

const { NOTIFICATIONS_LIST_PAGINATION } = SERVER_FETCH_CONSTANTS;

export interface UseInboxResult {
  /** Unified, ordered list the page renders through one VirtualList (Task 15). */
  items: InboxItem[];
  /**
   * Chat-owned first-load flag (`messages_screen.dart:318/460`), surfaced as-is.
   * The page's spinner gate uses BOTH this AND `isFetchingChatNodes` (Task 15),
   * matching Flutter's `_isFetchingChatItems && _isFirstLoad` (line 695).
   */
  isFirstLoad: boolean;
  /** True while the chat first-page fetch is in flight (spinner gate, Task 15). */
  isFetchingChatNodes: boolean;
  /** All four feeds empty — the page's all-empty placeholder gate (Task 15). */
  isEmpty: boolean;
  /** "More updates" tap target (refills from the collapsed cache or fetches). */
  getNotifications: () => Promise<void>;
  /** Collapse the expanded notifications back to one page. */
  collapseNotifications: () => void;
  /** `onReachedEnd` target for the page's VirtualList (Task 15). */
  loadMoreChatNodes: () => Promise<void>;
  /**
   * Combined TopBar unseen-badge truth: any unread notification OR unread chat.
   * The TopBar reads THIS (via `useInboxContext`) instead of the
   * `INBOX_UNSEEN_BADGE` event bus, so every cached `HomeTopBarHeader` instance
   * (Feed + Inbox, both kept alive by `KeepAliveOutlet`) renders the SAME live
   * value and re-renders together when it flips — no per-instance stale state and
   * no late-subscriber replay gap (finding 5).
   */
  hasUnseenInbox: boolean;
  /**
   * Notification-tap mark-read (gated write + flip-on-success-only optimistic
   * update). `InboxPage` calls `void markNotificationRead(notification)` right
   * after dispatching the tap, mirroring `messages_screen.dart:659-673`.
   */
  markNotificationRead: (notification: AppNotificationEntity) => Promise<void>;
  /**
   * Re-fetch the current user into encrypted local storage. `InboxPage` threads
   * this into the notification-tap dispatcher's context, from where the
   * `reportUser` handler invokes it (`push_navigation_service.dart:184-193`).
   */
  refreshCurrentUser: () => Promise<void>;
}

export function useInbox(): UseInboxResult {
  // Auth gate. The composer owns it; both sub-hooks stay parameterized on the id.
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.firebaseId ?? null;

  const {
    notifications,
    hasMoreNotifications,
    isFetchingNotifications,
    hasUnreadNotification,
    getNotifications,
    collapseNotifications,
    markNotificationRead,
  } = useInboxNotifications({ currentUserId });

  const {
    chatNodes,
    hasMoreChatNodes,
    isFetchingChatNodes,
    isFirstLoad,
    hasUnreadMessage,
    getChatNodes,
    loadMoreChatNodes,
  } = useInboxChat({ currentUserId });

  // Fire-once startup-init guard keyed on the resolved owner. Stays `null` until
  // a user is present; the guard records WHICH user it initialized for, so a
  // later sign-in (null → id) still primes the inbox + badge, while StrictMode's
  // double-mount and benign re-renders do not re-fetch. This replaces the old
  // page-driven `onPageInit` — opening `/inbox` no longer re-fires either
  // first-page fetch, because the single shared core has already run it.
  const initializedForRef = useRef<string | null>(null);

  /**
   * Combined TopBar unseen-badge truth — `_checkNotificationsUpdatesBadge` /
   * `_checkChatUpdatesBadge` (`messages_screen.dart:574-613`) reduced to a single
   * derived predicate. The TopBar reads this DIRECTLY off the shared context
   * (`useInboxContext`), which is the live single source: because
   * `InboxProvider` is mounted above the `KeepAliveOutlet` route cache, every
   * cached `HomeTopBarHeader` (Feed + Inbox) reads the SAME value and re-renders
   * together when it flips. This replaces the former per-instance
   * `useInboxUnseenBadge` event-bus subscription, whose `useState` started at
   * `false` and never replayed the last emit to a late-mounting subtree — which
   * was the Feed↔Inbox on/off loop (finding 5). With that subscriber removed the
   * `INBOX_UNSEEN_BADGE` event has no remaining listeners, so its emit and bus
   * key are gone too (the badge is now plain shared derived state, the web
   * analogue of Flutter's single `NewChatMessageNotifier`).
   */
  const hasUnseenInbox = hasUnreadNotification || hasUnreadMessage;

  /**
   * Web port of `UsersRepository.refreshUser()`
   * (`users_repository.dart:143-152`): re-fetch the current user by id and
   * persist it locally. Invoked (un-awaited, like Flutter) by the notification
   * tap's `reportUser` handler — `push_navigation_service.dart:184-193` calls it
   * only when the notification was unread, and that `!read` gate lives in the
   * handler, not here.
   *
   * Reuses `refreshAuthUserUseCase`, which writes ONLY to SecureStorage;
   * `AuthUserListener` mirrors that into Redux, so this must NOT dispatch
   * `setUser` (`state-slices.md`). Guarded on the composer's already-resolved
   * `currentUserId` auth gate.
   */
  const refreshCurrentUser = useCallback(async (): Promise<void> => {
    if (!currentUserId) return;
    await refreshAuthUserUseCase.execute(currentUserId);
  }, [currentUserId]);

  /**
   * Startup init. Reproduces Flutter `_initData` ordering
   * (`messages_screen.dart:95-104`): fire `getChatNodes()` WITHOUT awaiting (so
   * the chat fetch — which owns and clears `isFirstLoad` — drives the first-load
   * spinner), then `getNotifications()`. Runs ONCE per resolved user id at
   * provider mount (the web analogue of Flutter's always-mounted
   * `MessagesScreen` in the `MainScreen` `IndexedStack`), NOT on `/inbox` mount —
   * so opening the Inbox never produces a second read of either first page. The
   * badge emit follows from the effect above once either predicate settles,
   * matching the trailing `_checkNotificationsUpdatesBadge()` call. The
   * notification live-subscribe (`_subscribeToNotificationsUpdates`) is
   * intentionally skipped per Task 8's documented decision (live notifications
   * are deferred to a separate branch).
   *
   * Does NOT clear `isFirstLoad` here — the chat sub-hook clears it when its
   * first-load fetch settles (`messages_screen.dart:318/460`).
   */
  useEffect(() => {
    if (!currentUserId) return;
    if (initializedForRef.current === currentUserId) return;
    initializedForRef.current = currentUserId;
    void getChatNodes();
    void getNotifications();
  }, [currentUserId, getChatNodes, getNotifications]);

  /**
   * Unified item list reproducing the Flutter sliver order
   * (`messages_screen.dart:704-768`):
   *   [notificationsHeader?, ...notifications, moreNotifications?, chatHeader?, ...chatNodes]
   * Header / "more" items are built ONLY when their Flutter visibility predicate
   * would render non-empty, so the page never renders empty rows:
   *   - notifications header: `hasMoreItems || hasItems` (notifications_list_header.dart:15)
   *   - more notifications:   `hasItems && (hasMoreItems || itemsCount > NOTIFICATIONS_LIST_PAGINATION)`
   *                           (more_notifications_widget.dart:30-33)
   *   - chat header:          `hasMoreItems || hasItems` (chat_list_header.dart:19)
   */
  const items = useMemo<InboxItem[]>(() => {
    const list: InboxItem[] = [];

    const notificationsHasItems = notifications.length > 0;
    // notifications header
    if (hasMoreNotifications || notificationsHasItems) {
      list.push({
        type: InboxItemType.notificationsHeader,
        hasMoreItems: hasMoreNotifications,
        hasItems: notificationsHasItems,
      });
    }

    // notifications
    for (const notification of notifications) {
      list.push({ type: InboxItemType.notification, notification });
    }

    // "More updates" toggle. The pagination-threshold rule is owned here (not in
    // the row component): computed once and reused for both the push-gate and the
    // `canCollapse` flag threaded down to MoreNotificationsRow.
    const canCollapseNotifications =
      notifications.length > NOTIFICATIONS_LIST_PAGINATION;
    if (
      notificationsHasItems &&
      (hasMoreNotifications || canCollapseNotifications)
    ) {
      list.push({
        type: InboxItemType.moreNotifications,
        hasMoreItems: hasMoreNotifications,
        hasItems: notificationsHasItems,
        isFetching: isFetchingNotifications,
        canCollapse: canCollapseNotifications,
      });
    }

    const chatHasItems = chatNodes.length > 0;
    // chat header
    if (hasMoreChatNodes || chatHasItems) {
      list.push({
        type: InboxItemType.chatHeader,
        notificationsEmpty: !hasMoreNotifications && notifications.length === 0,
        hasMoreItems: hasMoreChatNodes,
        hasItems: chatHasItems,
      });
    }

    // chat nodes
    for (const node of chatNodes) {
      list.push({ type: InboxItemType.chatMessage, node });
    }

    return list;
  }, [
    notifications,
    hasMoreNotifications,
    isFetchingNotifications,
    chatNodes,
    hasMoreChatNodes,
  ]);

  // All-empty placeholder predicate (`messages_screen.dart:771-774`).
  const isEmpty =
    !hasMoreNotifications &&
    notifications.length === 0 &&
    !hasMoreChatNodes &&
    chatNodes.length === 0;

  return {
    items,
    isFirstLoad,
    isFetchingChatNodes,
    isEmpty,
    getNotifications,
    collapseNotifications,
    loadMoreChatNodes,
    hasUnseenInbox,
    markNotificationRead,
    refreshCurrentUser,
  };
}
